import { isAxiosError } from "axios"

import { responseErrorCode } from "./pricing-errors"

/** Utility save: the Genability account was set back, so nothing changed. */
export const SOLAR_UTILITY_SAVE_FAILED_CODE = `SOLAR_UTILITY_SAVE_FAILED`
/** Utility save: the Genability account could not be set back. */
export const SOLAR_UTILITY_RESTORE_FAILED_CODE = `SOLAR_UTILITY_RESTORE_FAILED`
export const SOLAR_FINANCING_LOCKED_CODE = `SOLAR_FINANCING_LOCKED`
/** Incentives read and refresh: System Pricing is not stored yet. */
export const SOLAR_SYSTEM_PRICING_REQUIRED_CODE = `SOLAR_SYSTEM_PRICING_REQUIRED`

export const SAVINGS_SUPERSEDED_CODE = `SAVINGS_SUPERSEDED`
export const SAVINGS_SUPERSEDED_MESSAGE = `Inputs changed while recalculating. Try again.`
export const SOLAR_SAVINGS_NO_ACTIVE_DESIGN_CODE = `SOLAR_SAVINGS_NO_ACTIVE_DESIGN`
export const SOLAR_SAVINGS_NOT_READY_CODE = `SOLAR_SAVINGS_NOT_READY`

/** The `error` code a Solar Proposal command answered with, when it is one. */
export const solarProposalErrorCode = (error: unknown): string | null => {
  const code = responseErrorCode(error)
  return typeof code === `string` ? code : null
}

/** Finance Partner & Equipment: every reason the save or preview was refused. */
export const SOLAR_PRODUCT_OFFERING_INVALID_CODE = `SOLAR_PRODUCT_OFFERING_INVALID`
/** The proposal, its Site or its design changed while the save checked it. */
export const SOLAR_PRODUCT_OFFERING_SUPERSEDED_CODE = `SOLAR_PRODUCT_OFFERING_SUPERSEDED`
/** Compute: the chosen node isn't offered (or catalogued) for the proposal now. */
export const SOLAR_COMPUTE_INVALID_CODE = `SOLAR_COMPUTE_INVALID`

/**
 * The reasons a Solar Proposal command gave: the gateway lists several in
 * `message` (validation and constraint failures), or gives one.
 */
export const solarProposalErrorMessages = (error: unknown): string[] => {
  if (!isAxiosError(error)) return []
  const message: unknown = error.response?.data?.message
  if (Array.isArray(message)) {
    return message.filter(
      (entry): entry is string => typeof entry === `string` && entry !== ``,
    )
  }
  return typeof message === `string` && message.trim() ? [message] : []
}

/** Designer save: the design changed elsewhere since the editor loaded it. */
export const SOLAR_DESIGN_SUPERSEDED_CODE = `SOLAR_DESIGN_SUPERSEDED`

/** Design version switch or duplicate: the selected Finance Partner doesn't offer its equipment. */
export const SOLAR_DESIGN_VERSION_EQUIPMENT_UNAVAILABLE_CODE = `SOLAR_DESIGN_VERSION_EQUIPMENT_UNAVAILABLE`
/** Design version switch or duplicate: the equipment check needs the utility rate first. */
export const SOLAR_DESIGN_VERSION_NO_UTILITY_CODE = `SOLAR_DESIGN_VERSION_NO_UTILITY`

/** Refresh Pricing refused a Credit Human plan: no loan term, or one no longer offered. */
export const SOLAR_LOAN_PRODUCT_REQUIRED_CODE = `SOLAR_LOAN_PRODUCT_REQUIRED`
export const SOLAR_LOAN_PRODUCT_UNAVAILABLE_CODE = `SOLAR_LOAN_PRODUCT_UNAVAILABLE`
/** Refresh Pricing refused a Credit Human plan whose partner is no longer offered. */
export const SOLAR_CREDIT_HUMAN_NOT_OFFERED_CODE = `SOLAR_CREDIT_HUMAN_NOT_OFFERED`
/** Refresh Pricing refused a Sungage plan: Sungage isn't offered with its funding group. */
export const SOLAR_SUNGAGE_NOT_OFFERED_CODE = `SOLAR_SUNGAGE_NOT_OFFERED`
/** Refresh Pricing refused a Sungage plan with no funding group. */
export const SOLAR_SUNGAGE_FUNDING_GROUP_MISSING_CODE = `SOLAR_SUNGAGE_FUNDING_GROUP_MISSING`
/** Refresh Pricing: Sungage's `/payments` failed; nothing was written. The message is V1's reason. */
export const SOLAR_SUNGAGE_PROVIDER_FAILED_CODE = `SOLAR_SUNGAGE_PROVIDER_FAILED`
/** Refresh Pricing: Sungage refused the seller (401/403); nothing was written. */
export const SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED_CODE = `SOLAR_SUNGAGE_SELLER_NOT_CERTIFIED`

/** Credit Human Apply / Replace: the Quote isn't current; refresh pricing first. */
export const SOLAR_REFRESH_PRICING_REQUIRED_CODE = `SOLAR_REFRESH_PRICING_REQUIRED`

export const isSolarRefreshPricingRequiredError = (error: unknown): boolean =>
  solarProposalErrorCode(error) === SOLAR_REFRESH_PRICING_REQUIRED_CODE

/** Concert offer save: the plan isn't Concert, or Concert isn't offered here. */
export const SOLAR_CONCERT_NOT_OFFERED_CODE = `SOLAR_CONCERT_NOT_OFFERED`
/** Concert offer save: Concert no longer returns that offer. */
export const SOLAR_CONCERT_OFFER_UNAVAILABLE_CODE = `SOLAR_CONCERT_OFFER_UNAVAILABLE`
/** Concert offer save: the price is outside the configured Concert range; the message names the bound. */
export const SOLAR_CONCERT_AMOUNT_OUT_OF_RANGE_CODE = `SOLAR_CONCERT_AMOUNT_OUT_OF_RANGE`
/** Concert offer save: no offers for today's price; Refresh Pricing first. */
export const SOLAR_CONCERT_PRICING_REQUIRED_CODE = `SOLAR_CONCERT_PRICING_REQUIRED`
/** Concert Initiate / submit: the seller's Concert user isn't set up. */
export const SOLAR_CONCERT_SELLER_NOT_READY_CODE = `SOLAR_CONCERT_SELLER_NOT_READY`
/** Concert Initiate / submit refused for a reason its message gives. */
export const SOLAR_CONCERT_APPLICATION_REFUSED_CODE = `SOLAR_CONCERT_APPLICATION_REFUSED`

/** Get LightReach pricing / select: production, savings or the optimized estimate isn't current. */
export const SOLAR_LIGHTREACH_PRICING_NOT_READY_CODE = `SOLAR_LIGHTREACH_PRICING_NOT_READY`
/**
 * The LightReach offers or the selected offer no longer match today's
 * pricing: Get LightReach pricing, then choose an offer. Refresh Pricing
 * answers it for a LightReach plan too.
 */
export const SOLAR_LIGHTREACH_PRICING_REQUIRED_CODE = `SOLAR_LIGHTREACH_PRICING_REQUIRED`
/** Select: the back-solved margin is outside what the seller can set. */
export const SOLAR_LIGHTREACH_MARGIN_OUT_OF_BOUNDS_CODE = `SOLAR_LIGHTREACH_MARGIN_OUT_OF_BOUNDS`
/** Select: the offer isn't among the stored LightReach offers. */
export const SOLAR_LIGHTREACH_OFFER_UNAVAILABLE_CODE = `SOLAR_LIGHTREACH_OFFER_UNAVAILABLE`
/** The proposal fails LightReach's rules; `message` lists every reason. */
export const SOLAR_LIGHTREACH_INELIGIBLE_CODE = `SOLAR_LIGHTREACH_INELIGIBLE`
/** The plan isn't LightReach, or LightReach isn't offered here now. */
export const SOLAR_LIGHTREACH_NOT_OFFERED_CODE = `SOLAR_LIGHTREACH_NOT_OFFERED`
/** Another LightReach write for this proposal holds the lock. */
export const SOLAR_LIGHTREACH_BUSY_CODE = `SOLAR_LIGHTREACH_BUSY`
/** Send contract: approval, a current offer or savings is missing. */
export const SOLAR_LIGHTREACH_CONTRACT_NOT_READY_CODE = `SOLAR_LIGHTREACH_CONTRACT_NOT_READY`
/** LightReach failed or timed out; nothing was stored. */
export const SOLAR_LIGHTREACH_PROVIDER_FAILED_CODE = `SOLAR_LIGHTREACH_PROVIDER_FAILED`

export const isSolarLightReachPricingRequiredError = (error: unknown): boolean =>
  solarProposalErrorCode(error) === SOLAR_LIGHTREACH_PRICING_REQUIRED_CODE

/** The first reason a Solar command gave, joined when it gave several. */
export const solarProposalErrorMessage = (error: unknown): string | null => {
  const messages = solarProposalErrorMessages(error)
  return messages.length > 0 ? messages.join(`; `) : null
}
